import React, { useEffect, useState } from "react";
import "./LoginPage.css";
import Logo from "../../components/logo/Logo";
import OTP from "../../components/otp/OTPInput";
import Notification from "../../components/notification/Notification";
import { useSearchParams } from "react-router-dom";
import { getAuthUrl } from "../../utils";
import FloatingMessage from "../../components/floating-message/FloatingMessage";

function LoginPage() {
  const [searchParams] = useSearchParams();
  const [isAgent, setIsAgent] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState("");

  useEffect(() => {
    if (searchParams.get("auth-type") === "agent") {
      setIsAgent(true);
    }
    setRedirectUrl(searchParams.get("redirect_url"));
  }, [setIsAgent, searchParams]);

  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
    code: "",
    redirect_url: searchParams.get("redirect_url"),
  });

  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setCredentials((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const updateOTP = (value) => {
    setCredentials((prevState) => ({
      ...prevState,
      code: value,
    }));
  };

  const submit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    fetch(getAuthUrl(isAgent) + "/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(credentials),
    })
      .then((res) => res.json())
      .then((json) => {
        if (json.hasOwnProperty("error")) {
          setNotificationType("error");
          if (json["error"] === "internal server error") {
            setNotificationMessage(
              "Une erreur inattendue s'est produite. Veuillez réessayer plus tard. Si le problème persiste, n'hésitez pas à contacter notre support client."
            );
          } else if (json["error"] === "invalid 2FA code") {
            if (credentials.code === "") {
              setNotificationMessage("Code 2FA manquant.");
            } else {
              setNotificationMessage("Code 2FA invalide.");
            }
          } else {
            setNotificationMessage("Email ou mot de passe invalide.");
          }
          setShowNotification(true);
          setIsLoading(false);
        } else {
          setNotificationType("success");
          setNotificationMessage("You're successfully connected !");
          setShowNotification(true);

          setTimeout(() => {
            setIsLoading(false);

            const queryParameters = new URLSearchParams(window.location.search);
            window.location.href =
              queryParameters.get("redirect_url") + "?token=" + json["token"];
          }, 500);
        }
      })
      .catch((error) => {
        console.error(error);
        setNotificationType("error");
        setNotificationMessage(
          "Une erreur inattendue s'est produite. Veuillez réessayer plus tard. Si le problème persiste, n'hésitez pas à contacter notre support client."
        );
        setShowNotification(true);
        setIsLoading(false);
      });
  };

  const clearNotification = () => {
    setTimeout(() => {
      setShowNotification(false);
    }, 350);
  };

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="cp-bg-gray mt-10 sm:rounded-lg sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div className="bg-white py-4 px-16 m-8 rounded-lg">
          <Logo />
        </div>

        <h2 className="mt-6 text-center text-2xl font-bold leading-9 tracking-tight text-gray-100">
          Connexion
        </h2>

        {
          /* Hide floating message on production mode */
          process.env.NODE_ENV !== "production" && (
            <FloatingMessage></FloatingMessage>
          )
        }

        {showNotification ? (
          <Notification
            done={clearNotification}
            type={notificationType}
            message={notificationMessage}
          ></Notification>
        ) : (
          <></>
        )}

        <div className="px-6 py-12 shadow sm:px-12">
          <form
            className="space-y-6"
            action={null}
            method="POST"
            onSubmit={submit}
          >
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium leading-6 text-gray-100"
              >
                Email{" "}
                <span className="text-orange-600 font-bold text-lg">*</span>
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  onChange={handleInputChange}
                  className="block w-full rounded-md border-0 py-1.5 pl-2 text-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium leading-6 text-gray-100"
              >
                Mot de passe{" "}
                <span className="text-orange-600 font-bold text-lg">*</span>
              </label>
              <div className="mt-2">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  onChange={handleInputChange}
                  className="block w-full rounded-md border-0 py-1.5 pl-2 text-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="otp"
                className="block text-sm font-medium leading-6 text-gray-100"
              >
                Code 2FA (Optionnel)
              </label>
              <OTP onOtpChange={updateOTP}></OTP>
            </div>

            <div className="pt-8">
              <button
                type="submit"
                className="mt-0 flex w-full justify-center rounded-md cp-bg-orange px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:cp-bg-orange-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                {isLoading ? (
                  <div
                    className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-current border-e-transparent text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white mr-2"
                    role="status"
                  >
                    <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]"></span>
                  </div>
                ) : (
                  <>Connexion</>
                )}
              </button>
              <a
                href={
                  "/request-reset?redirect_url=" +
                  redirectUrl +
                  (isAgent ? "&auth-type=agent" : "")
                }
                className="font-semibold text-gray-100 hover:cp-text-orange text-xs"
              >
                Mot de passe oublié ?
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
